<template>
	<div>
		<PageHeader
			:data="{
				name: 'Client Recurrence',
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Clients', to: '/clients' },
				],
			}"
		/>

		<div class="row mb-3">
			<!-- Recurring summary -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2">Recurring Summary</h4>
								<span v-if="summaryTruncated" class="badge bg-warning text-dark">Partial data</span>
							</div>
							<div class="d-flex flex-wrap align-items-center gap-2">
								<div class="d-flex align-items-center gap-2">
									<label class="form-label m-0 small text-muted">Custom range start</label>
									<input
										type="date"
										class="form-control form-control-sm"
										:max="customRangeEnd || maxContractEndDate || null"
										v-model="customRangeStart"
									/>
									<label class="form-label m-0 small text-muted">Custom range end</label>
									<input
										type="date"
										class="form-control form-control-sm"
										:min="customRangeStart || todayDate"
										v-model="customRangeEnd"
									/>
								</div>
							</div>
						</div>

						<div v-if="summaryLoading" class="d-flex justify-content-center py-3">
							<div class="spinner-border spinner-border-sm" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
						<div v-else class="row g-2 mt-1">
							<div class="col-12 col-md-4">
								<div class="summary-card">
									<div class="summary-label">Current recurring (annual)</div>
									<div class="summary-value">
										{{ formatCurrency(summaryTotals.current, summaryCurrencyCode) }}
										<a href="#" class="small text-primary ms-2" @click.prevent="downloadRecurringExport('current')">
											<span v-if="isExporting('current')" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
											<font-awesome-icon v-else :icon="['fas', 'download']" />
										</a>
									</div>
									<div class="summary-subtext">Sum of annualized recurring templates</div>
								</div>
							</div>
							<div class="col-12 col-md-4">
								<div class="summary-card">
									<div class="summary-label">All recurring (contract total)</div>
									<div class="summary-value">
										{{ formatCurrency(summaryTotals.all, summaryCurrencyCode) }}
										<a href="#" class="small text-primary ms-2" @click.prevent="downloadRecurringExport('all')">
											<span v-if="isExporting('all')" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
											<font-awesome-icon v-else :icon="['fas', 'download']" />
										</a>
									</div>
									<div class="summary-subtext">Annualized × contract years (or ×1 if missing)</div>
								</div>
							</div>
							<div class="col-12 col-md-4">
								<div class="summary-card">
									<div class="summary-label">
										Custom range ({{ customRangeLabel }})
									</div>
									<div class="summary-value">
										{{ formatCurrency(summaryTotals.rangeUnder, summaryCurrencyCode) }}
										<a href="#" class="small text-primary ms-2" @click.prevent="downloadRecurringExport('custom')">
											<span v-if="isExporting('custom')" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
											<font-awesome-icon v-else :icon="['fas', 'download']" />
										</a>
									</div>
									<div class="summary-subtext">
										Under contract: {{ formatCurrency(summaryTotals.rangeUnder, summaryCurrencyCode) }} ·
										Out of contract: {{ formatCurrency(summaryTotals.rangeOver, summaryCurrencyCode) }}
									</div>
								</div>
							</div>
						</div>
						<small v-if="summaryTruncated" class="text-muted">
							Summary uses the first {{ summaryLimit }} items. Narrow filters for exact totals.
						</small>
					</div>
				</div>
			</div>

			<!-- Filters -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2"><font-awesome-icon :icon="['fas', 'filter']" class="text-primary me-1" /> Filters</h4>
								<button v-if="hasActiveFilters()" @click="clearFilters()" class="btn btn-sm btn-danger text-white me-2">
									Clear
									<font-awesome-icon :icon="['fas', 'times']" />
								</button>
							</div>
						</div>

						<LPDataTableFilters :filters="table.filters" v-on:getFilters="getFilters($event.filters)" />
					</div>
				</div>
			</div>

			<!-- Table data -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center mb-3">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2">Recurring Invoices</h4>
							</div>
							<div class="action-buttons">
								<button class="btn btn-outline-primary btn-sm me-2" @click="downloadRecurringExport('table')" :disabled="exportLoading">
									<span v-if="isExporting('table')" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
									<font-awesome-icon v-else :icon="['fas', 'file-csv']" class="me-1" />
									Download filtered
								</button>
								<button class="btn btn-primary btn-sm me-2" @click="startSyncAll" :disabled="syncAllLoading">
									<span v-if="syncAllLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
									<font-awesome-icon v-else :icon="['fas', 'sync']" class="me-1" />
									Sync All
								</button>
								<button class="btn btn-outline-secondary btn-sm me-2" @click="checkDuplicates" :disabled="duplicatesLoading">
									<span v-if="duplicatesLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
									<font-awesome-icon v-else :icon="['fas', 'copy']" class="me-1" />
									Check Duplicates
								</button>
								<button
									data-bs-toggle="offcanvas"
									data-bs-target="#offcanvasTableSettings"
									aria-controls="offcanvasTableSettings"
									class="btn btn-outline-secondary btn-sm"
								>
									Table Settings <font-awesome-icon :icon="['fas', 'cogs']" class="me-1" />
								</button>
							</div>
						</div>
						<table :class="!loading && recurringInvoices.length > 0 ? 'table table-hover' : 'table table-hover loading'">
							<thead>
								<tr>
									<th v-if="userColumns.includes('profile_id')" width="100">Profile ID</th>
									<th v-if="userColumns.includes('client_name')">Client Name</th>
									<th v-if="userColumns.includes('organization')">Organization</th>
									<th v-if="userColumns.includes('description')">Description</th>
									<th v-if="userColumns.includes('frequency')">Frequency</th>
									<th v-if="userColumns.includes('amount')" width="120">Amount</th>
									<th v-if="userColumns.includes('create_date')" width="120">Create Date</th>
									<th v-if="userColumns.includes('contract_start_at')" width="130">Contract Start</th>
									<th v-if="userColumns.includes('contract_ends_at')" width="130">Contract End</th>
									<th v-if="userColumns.includes('contract_remaining')" width="140">Remaining</th>
									<th v-if="userColumns.includes('status')" width="100">Status</th>
									<th width="120">Actions</th>
								</tr>
							</thead>
							<tbody v-if="recurringInvoices.length > 0 && !loading">
								<tr
									v-for="recurring in recurringInvoices"
									:key="recurring.id"
									:class="[{ 'bg-secondary-lighter': recurring.disable }, { 'row-missing-client': recurring.client_missing }]"
								>
									<td v-if="userColumns.includes('profile_id')" class="align-middle">
										{{ recurring.profile_id }}
									</td>
									<td v-if="userColumns.includes('client_name')" class="align-middle">
										<router-link
											v-if="recurring.client_id"
											:to="'/clients/edit/' + recurring.client_id"
											class="text-decoration-none"
										>
											{{ recurring.client_name }}
										</router-link>
										<div v-else>
											<span class="badge bg-danger">Missing client</span>
										</div>
									</td>
									<td v-if="userColumns.includes('organization')" class="align-middle">
										<div>{{ recurring.organization }}</div>
										<small class="text-muted">
											{{ recurring.first_name }} {{ recurring.last_name }}
										</small>
									</td>
									<td v-if="userColumns.includes('description')" class="align-middle">
										<small>{{ truncateText(recurring.description, 50) }}</small>
									</td>
									<td v-if="userColumns.includes('frequency')" class="align-middle">
										<span class="badge badge-info">
											{{ getFrequencyLabel(recurring.frequency) }}
										</span>
									</td>
									<td v-if="userColumns.includes('amount')" class="align-middle">
										<strong>{{ formatCurrency(recurring.amount, recurring.currency_code) }}</strong>
									</td>
									<td v-if="userColumns.includes('create_date')" class="align-middle">
										{{ formatDate(recurring.create_date) }}
									</td>
									<td v-if="userColumns.includes('contract_start_at')" class="align-middle">
										{{ formatDateOrTilde(recurring.contract_start_at) }}
									</td>
									<td v-if="userColumns.includes('contract_ends_at')" class="align-middle">
										{{ formatDateOrTilde(recurring.contract_ends_at) }}
									</td>
									<td v-if="userColumns.includes('contract_remaining')" class="align-middle">
										{{ formatRemaining(recurring.contract_remaining) }}
									</td>
									<td v-if="userColumns.includes('status')" class="align-middle">
										<span
											class="badge"
											:class="recurring.disable ? 'badge-secondary' : 'badge-success'"
										>
											{{ recurring.disable ? 'Disabled' : 'Active' }}
										</span>
									</td>
									<td class="align-middle">
										<button class="btn btn-outline-primary btn-sm" @click="openRecurrenceModal(recurring)">
											<font-awesome-icon :icon="['fas', 'eye']" class="me-1" /> View
										</button>
									</td>
								</tr>
							</tbody>
						</table>
						<div v-if="!loading && recurringInvoices.length === 0" class="col-md-12 text-center">
							<h4>No data available</h4>
						</div>
						<div v-if="loading && recurringInvoices.length === 0" class="d-flex justify-content-center">
							<div class="spinner-border" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
						<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
					</div>
				</div>
			</div>
		</div>

		<!-- Recurrence Preview Modal -->
		<div class="modal fade" id="recurrencePreviewModal" tabindex="-1" aria-labelledby="recurrencePreviewModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="recurrencePreviewModalLabel">Recurrence Preview</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div v-if="selectedRecurring">
							<div class="row g-2">
								<div class="col-12 col-md-6">
									<div class="detail-label">Client</div>
									<div class="detail-value">{{ selectedRecurring.client_name || 'N/A' }}</div>
								</div>
								<div class="col-12 col-md-6">
									<div class="detail-label">Profile ID</div>
									<div class="detail-value">{{ selectedRecurring.profile_id || 'N/A' }}</div>
								</div>
								<div class="col-12 col-md-6">
									<div class="detail-label">Freshbooks Client ID</div>
									<div class="detail-value">{{ selectedRecurring.freshbooks_customer_id || 'N/A' }}</div>
								</div>
								<div class="col-12 col-md-6">
									<div class="detail-label">Frequency</div>
									<div class="detail-value">{{ getFrequencyLabel(selectedRecurring.frequency) }}</div>
								</div>
								<div class="col-12 col-md-6">
									<div class="detail-label">Amount</div>
									<div class="detail-value">{{ formatCurrency(selectedRecurring.amount, selectedRecurring.currency_code) }}</div>
								</div>
								<div class="col-12 col-md-6">
									<div class="detail-label">Status</div>
									<div class="detail-value">{{ selectedRecurring.disable ? 'Disabled' : 'Active' }}</div>
								</div>
								<div class="col-12">
									<div class="detail-label">Description</div>
									<div class="detail-value">{{ selectedRecurring.description || 'N/A' }}</div>
								</div>
								<div class="col-12 col-md-6">
									<div class="detail-label">Created</div>
									<div class="detail-value">{{ formatDate(selectedRecurring.create_date) }}</div>
								</div>
							</div>

							<div class="mt-3">
								<div class="detail-label">Recurrence Details</div>
								<table class="table table-sm table-borderless mb-0">
									<tbody>
										<tr>
											<td class="detail-label">Profile ID</td>
											<td class="detail-value">{{ selectedRecurring.profile_id || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Client</td>
											<td class="detail-value">{{ selectedRecurring.client_name || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Client ID</td>
											<td class="detail-value">{{ selectedRecurring.client_id || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Freshbooks Client ID</td>
											<td class="detail-value">{{ selectedRecurring.freshbooks_customer_id || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Organization</td>
											<td class="detail-value">{{ selectedRecurring.organization || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Contact</td>
											<td class="detail-value">{{ selectedRecurring.first_name || 'N/A' }} {{ selectedRecurring.last_name || '' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Description</td>
											<td class="detail-value">{{ selectedRecurring.description || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Frequency</td>
											<td class="detail-value">{{ getFrequencyLabel(selectedRecurring.frequency) }}</td>
										</tr>
										<tr>
											<td class="detail-label">Translated Frequency</td>
											<td class="detail-value">{{ selectedRecurring.translated_frequency || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Amount</td>
											<td class="detail-value">{{ formatCurrency(selectedRecurring.amount, selectedRecurring.currency_code) }}</td>
										</tr>
										<tr>
											<td class="detail-label">Currency</td>
											<td class="detail-value">{{ selectedRecurring.currency_code || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Created</td>
											<td class="detail-value">{{ formatDate(selectedRecurring.create_date) }}</td>
										</tr>
										<tr>
											<td class="detail-label">Contract Start</td>
											<td class="detail-value">{{ formatDateOrTilde(selectedRecurring.contract_start_at) }}</td>
										</tr>
										<tr>
											<td class="detail-label">Contract End</td>
											<td class="detail-value">{{ formatDateOrTilde(selectedRecurring.contract_ends_at) }}</td>
										</tr>
										<tr>
											<td class="detail-label">Remaining</td>
											<td class="detail-value">{{ formatRemaining(selectedRecurring.contract_remaining) }}</td>
										</tr>
										<tr>
											<td class="detail-label">Status</td>
											<td class="detail-value">{{ selectedRecurring.disable ? 'Disabled' : 'Active' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Display Status</td>
											<td class="detail-value">{{ selectedRecurring.display_status || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Auto Bill</td>
											<td class="detail-value">{{ selectedRecurring.auto_bill ? 'Yes' : 'No' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Require Auto Bill</td>
											<td class="detail-value">{{ selectedRecurring.require_auto_bill ? 'Yes' : 'No' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Bill Gateway</td>
											<td class="detail-value">{{ selectedRecurring.bill_gateway || 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Infinite</td>
											<td class="detail-value">{{ selectedRecurring.is_infinite ? 'Yes' : 'No' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Number Recurring</td>
											<td class="detail-value">{{ selectedRecurring.number_recurring !== null && selectedRecurring.number_recurring !== undefined ? selectedRecurring.number_recurring : 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Occurrences To Date</td>
											<td class="detail-value">{{ selectedRecurring.occurrences_to_date !== null && selectedRecurring.occurrences_to_date !== undefined ? selectedRecurring.occurrences_to_date : 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Number Remaining</td>
											<td class="detail-value">{{ selectedRecurring.number_remaining !== null && selectedRecurring.number_remaining !== undefined ? selectedRecurring.number_remaining : 'N/A' }}</td>
										</tr>
										<tr>
											<td class="detail-label">Last Invoice</td>
											<td class="detail-value">{{ formatDateOrTilde(selectedRecurring.tracking_last_invoice_date) }}</td>
										</tr>
										<tr>
											<td class="detail-label">Last Occurrence</td>
											<td class="detail-value">{{ formatDateOrTilde(selectedRecurring.tracking_last_occurrence_date) }}</td>
										</tr>
										<tr>
											<td class="detail-label">Next Invoice</td>
											<td class="detail-value">{{ formatDateOrTilde(selectedRecurring.tracking_next_invoice_date) }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div v-else class="text-muted">No recurrence selected.</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Offcanvas for Table Settings -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasTableSettings" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Table settings</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<h4 class="m-0">Display columns</h4>
				<p class="p-0"><small>ℹ️ Select which columns to display in table</small></p>
				<div class="table-columns-settings">
					<div class="d-flex flex-wrap">
						<div :key="'column-available-' + column" v-for="column in availableColumns" class="mb-2 me-1">
							<span role="button" @click="toggleUserColumn(column)" v-if="userColumns.includes(column)" class="badge bg-success">{{ columnLabels[column] }}</span>
							<span role="button" @click="toggleUserColumn(column)" v-else class="badge bg-secondary">{{ columnLabels[column] }}</span>
						</div>
					</div>
				</div>

				<small>👨‍🏫 Selected columns are automatically saved to your browser storage and will be used again when you come on this page</small>

				<h4 class="mt-3">Display items per page</h4>
				<p class="p-0">
					<small>ℹ️ Select how many items to display per page in table</small>
				</p>
				<div class="form-group w-75">
					<select v-model="userPerPage" @change="setUserPerPage()" class="form-select">
						<option value="10">10 per page</option>
						<option value="15">15 per page</option>
						<option value="20">20 per page</option>
						<option value="30">30 per page</option>
						<option value="50">50 per page</option>
						<option value="100">100 per page</option>
					</select>
				</div>
			</div>
		</div>

		<!-- Sync All Modal -->
		<div class="modal fade" id="syncAllRecurrenceModal" tabindex="-1" aria-labelledby="syncAllRecurrenceModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="syncAllRecurrenceModalLabel">Sync All Recurrences</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="alert alert-warning">
							This will clear the table and pull active invoice profiles from Freshbooks.
						</div>

						<div v-if="syncAllLoading" class="text-muted">
							<div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
							Syncing... please wait.
						</div>

						<div v-if="syncAllError" class="alert alert-danger mt-3 mb-0">
							{{ syncAllError }}
						</div>

						<div v-if="syncAllStats" class="mt-3">
							<div class="detail-label">Sync Status</div>
							<div class="detail-value">
								Added: {{ syncAllStats.created || 0 }} · Updated: {{ syncAllStats.updated || 0 }} · Deleted: {{ syncAllStats.deleted || 0 }}
								<span v-if="syncAllStats.deleted_existing !== undefined"> · Cleared: {{ syncAllStats.deleted_existing }}</span>
								<span v-if="syncAllStats.unmatched_clients !== undefined"> · Missing clients: {{ syncAllStats.unmatched_clients }}</span>
								<span v-if="syncAllStats.skipped_inactive !== undefined"> · Skipped inactive: {{ syncAllStats.skipped_inactive }}</span>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Duplicates Modal -->
		<div class="modal fade" id="recurrenceDuplicatesModal" tabindex="-1" aria-labelledby="recurrenceDuplicatesModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="recurrenceDuplicatesModalLabel">Recurring Duplicates</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div v-if="duplicatesLoading" class="text-muted">
							<div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
							Checking duplicates...
						</div>

						<div v-if="duplicatesError" class="alert alert-danger mt-3 mb-0">
							{{ duplicatesError }}
						</div>

						<div v-if="!duplicatesLoading && duplicates.length === 0 && !duplicatesError" class="text-muted">
							No duplicates found.
						</div>

						<div v-if="duplicates.length > 0" class="table-responsive">
							<table class="table table-sm table-striped">
								<thead>
									<tr>
										<th>Name</th>
										<th width="140">Amount</th>
										<th width="90">Count</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="dup in duplicates" :key="`dup-${dup.profile_id}`">
										<td>{{ dup.name }}</td>
										<td>{{ formatCurrency(dup.amount, dup.currency_code) }}</td>
										<td>{{ dup.duplicate_count }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
import LPDataTableFilters from '@/components/lp-data-table/LPDataTableFilters'
import { Modal } from 'bootstrap'
import { mapState } from 'vuex'
import { handleErrors } from '../../actions/helpers'
import restApi from '../../api/index.js'

export default {
	name: 'ClientRecurrence',
	components: {
		PageHeader,
		LPDataTablePagination,
		LPDataTableFilters,
	},
	data() {
		return {
			loading: false,
			recurringInvoices: [],
			pagination: null,
			params: {},
			summaryLoading: false,
			summaryInvoices: [],
			summaryTotals: {
				current: 0,
				all: 0,
				rangeUnder: 0,
				rangeOver: 0,
			},
			summaryCurrencyCode: 'USD',
			summaryTruncated: false,
			summaryLimit: 10000,
			selectedRecurring: null,
			pullLoading: false,
			pullResponse: null,
			pullStats: null,
			pullError: '',
			recurrenceModal: null,
			syncAllLoading: false,
			syncAllStats: null,
			syncAllError: '',
			syncAllModal: null,
			duplicatesLoading: false,
			duplicates: [],
			duplicatesError: '',
			duplicatesModal: null,
			customRangeStart: '',
			customRangeEnd: '',
			maxContractEndDate: '',
			lastSummaryParams: '',
			exportLoading: false,
			exportingMode: '',
			userColumns: JSON.parse(localStorage.getItem('recurring-invoices-columns')) || [
				'profile_id',
				'client_name',
				'organization',
				'description',
				'frequency',
				'amount',
				'create_date',
				'contract_start_at',
				'contract_ends_at',
				'contract_remaining',
				'status',
			],
			availableColumns: ['profile_id', 'client_name', 'organization', 'description', 'frequency', 'amount', 'create_date', 'contract_start_at', 'contract_ends_at', 'contract_remaining', 'status'],
			columnLabels: {
				profile_id: 'Profile ID',
				client_name: 'Client Name',
				organization: 'Organization',
				description: 'Description',
				frequency: 'Frequency',
				amount: 'Amount',
				create_date: 'Create Date',
				contract_start_at: 'Contract Start',
				contract_ends_at: 'Contract End',
				contract_remaining: 'Remaining',
				status: 'Status',
			},
			userPerPage: localStorage.getItem('recurring-invoices-per-page') || 15,
			table: {
				filters: [
					{
						display: true,
						name: 'search',
						label: 'Search',
						type: 'text',
						placeholder: 'search by client name, organization, amount...',
						colClass: 'col-4',
						value: this.$route.query.search || '',
					},
					{
						display: true,
						name: 'frequency',
						label: 'Frequency',
						type: 'select',
						placeholder: 'All frequencies',
						colClass: 'col-2',
						value: this.$route.query.frequency || '',
						options: [
							{ id: 'y', name: 'Yearly' },
							{ id: 'm', name: 'Monthly' },
							{ id: '2w', name: 'Bi-Weekly' },
							{ id: 'w', name: 'Weekly' },
							{ id: 'q', name: 'Quarterly' },
						],
					},
					{
						display: true,
						name: 'auto_bill',
						label: 'Auto Bill',
						type: 'select',
						placeholder: 'All auto bill',
						colClass: 'col-2',
						value: this.$route.query.auto_bill || '',
						options: [
							{ id: 'true', name: 'Yes' },
							{ id: 'false', name: 'No' },
						],
					},
					{
						display: true,
						name: 'bill_gateway',
						label: 'Bill Gateway',
						type: 'select',
						placeholder: 'All gateways',
						colClass: 'col-2',
						value: this.$route.query.bill_gateway || '',
						options: [
							{ id: 'stripe', name: 'Stripe' },
							{ id: 'none', name: 'None' },
						],
					},
					{
						display: true,
						name: 'is_infinite',
						label: 'Infinite',
						type: 'select',
						placeholder: 'All',
						colClass: 'col-2',
						value: this.$route.query.is_infinite || '',
						options: [
							{ id: 'true', name: 'Yes' },
							{ id: 'false', name: 'No' },
						],
					},
				],
			},
		}
	},
	computed: {
		...mapState(['authUser']),
		todayDate() {
			return this.getLocalDateString(new Date())
		},
		customRangeLabel() {
			const start = this.customRangeStart || this.todayDate
			const end = this.customRangeEnd || this.maxContractEndDate
			if (end) {
				return `${start} → ${end}`
			}
			return `${start} → N/A`
		},
		formattedPullResponse() {
			if (!this.pullResponse) return ''
			return JSON.stringify(this.pullResponse, null, 2)
		},
	},
	watch: {
		'$route.query': {
			handler() {
				this.syncFiltersFromUrl()
				this.getRecurringInvoices()
				this.maybeRefreshSummary()
			},
			deep: true,
		},
		customRangeStart() {
			this.calculateSummaryTotals()
		},
		customRangeEnd() {
			this.calculateSummaryTotals()
		},
	},
	created() {
		this.customRangeStart = this.todayDate
		this.syncFiltersFromUrl()
		this.getRecurringInvoices()
		this.maybeRefreshSummary()
	},
	mounted() {
		this.recurrenceModal = new Modal('#recurrencePreviewModal')
		this.syncAllModal = new Modal('#syncAllRecurrenceModal')
		this.duplicatesModal = new Modal('#recurrenceDuplicatesModal')
	},
	methods: {
		checkDuplicates() {
			if (this.duplicatesLoading) return
			this.duplicatesError = ''
			this.duplicates = []
			if (this.duplicatesModal) this.duplicatesModal.show()

			this.duplicatesLoading = true
			restApi
				.get('/freshbooks/invoices/recurring/duplicates')
				.then(({ data }) => {
					this.duplicates = data.duplicates || []
				})
				.catch(error => {
					this.duplicatesError = handleErrors(error, 'Error checking duplicates')
				})
				.finally(() => {
					this.duplicatesLoading = false
				})
		},

		startSyncAll() {
			if (this.syncAllLoading) return
			this.syncAllError = ''
			this.syncAllStats = null
			if (this.syncAllModal) this.syncAllModal.show()

			this.syncAllLoading = true
			restApi
				.post('/freshbooks/invoices/recurring/sync-all')
				.then(({ data }) => {
					this.syncAllStats = data.stats || null
					this.getRecurringInvoices()
					this.maybeRefreshSummary()
				})
				.catch(error => {
					this.syncAllError = handleErrors(error, 'Error syncing recurring invoices')
				})
				.finally(() => {
					this.syncAllLoading = false
				})
		},

		openRecurrenceModal(recurring) {
			this.selectedRecurring = recurring
			this.pullResponse = null
			this.pullStats = null
			this.pullError = ''
			this.$nextTick(() => {
				if (this.recurrenceModal) this.recurrenceModal.show()
			})
		},

		pullRecurring() {
			if (!this.selectedRecurring || !this.selectedRecurring.freshbooks_customer_id) {
				this.pullError = 'Missing Freshbooks Client ID'
				return
			}

			this.pullLoading = true
			this.pullError = ''
			this.pullResponse = null

			restApi
				.get(`/freshbooks/invoices/recurring/fetch-by-freshbooks/${this.selectedRecurring.freshbooks_customer_id}`)
				.then(({ data }) => {
					this.pullResponse = data.freshbooks_response || data
					this.pullStats = data.stats || null
					this.getRecurringInvoices()
					this.maybeRefreshSummary()
				})
				.catch(error => {
					this.pullError = handleErrors(error, 'Error pulling recurring invoices')
				})
				.finally(() => {
					this.pullLoading = false
				})
		},

		getSummaryParams() {
			const params = {}
			if (this.$route.query.search) params.search = this.$route.query.search
			// Summary is always based on yearly + active recurring
			params.frequency = 'y'
			params.status = 'active'
			return params
		},

		maybeRefreshSummary() {
			const params = this.getSummaryParams()
			const paramsKey = JSON.stringify(params)
			if (paramsKey !== this.lastSummaryParams) {
				this.lastSummaryParams = paramsKey
				this.getRecurringSummary(params)
			}
		},

		isExporting(mode) {
			return this.exportLoading && this.exportingMode === mode
		},

		getRecurringExportParams(mode) {
			const baseParams = mode === 'table' ? { ...this.params } : this.getSummaryParams()
			const params = {
				...baseParams,
				mode,
				columns: this.userColumns,
			}

			if (mode === 'custom') {
				params.custom_range_start = this.customRangeStart || this.todayDate
				if (this.customRangeEnd) {
					params.custom_range_end = this.customRangeEnd
				}
			}

			return params
		},

		getRecurringExportFilename(mode, headers = {}) {
			const disposition = headers['content-disposition'] || headers['Content-Disposition']
			if (disposition) {
				const match = disposition.match(/filename="?([^"]+)"?/i)
				if (match && match[1]) {
					return match[1]
				}
			}
			return `recurring-${mode}-${this.todayDate}.csv`
		},

		downloadRecurringExport(mode) {
			if (this.exportLoading) return
			this.exportLoading = true
			this.exportingMode = mode

			const params = this.getRecurringExportParams(mode)

			restApi
				.get('/freshbooks/invoices/recurring/export', {
					params,
					responseType: 'blob',
				})
				.then(response => {
					const blob = new Blob([response.data], { type: 'text/csv' })
					const url = window.URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.setAttribute('download', this.getRecurringExportFilename(mode, response.headers))
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					window.URL.revokeObjectURL(url)
				})
				.catch(error => {
					console.error(error)
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'Error exporting recurring invoices'),
						timer: 6000,
					})
				})
				.finally(() => {
					this.exportLoading = false
					this.exportingMode = ''
				})
		},

		// Sync filters from URL query params
		syncFiltersFromUrl() {
			this.table.filters.forEach(filter => {
				if (this.$route.query[filter.name]) {
					filter.value = this.$route.query[filter.name]
					this.params[filter.name] = this.$route.query[filter.name]
				}
			})
		},

		// Get Recurring Invoices
		getRecurringInvoices() {
			this.loading = true
			const requestParams = {
				...this.params,
				page: parseInt(this.$route.query.page) || 1,
				perPage: this.userPerPage,
			}
			restApi
				.get(`/freshbooks/invoices/recurring`, { params: requestParams })
				.then(({ data }) => {
					this.recurringInvoices = data.data
					this.pagination = data
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'Error loading recurring invoices'),
						timer: 6000,
					})
				})
		},

		// Get summary data for recurring invoices
		getRecurringSummary(params = {}) {
			this.summaryLoading = true
			const requestParams = {
				...params,
				page: 1,
				perPage: this.summaryLimit,
			}
			restApi
				.get(`/freshbooks/invoices/recurring`, { params: requestParams })
				.then(({ data }) => {
					this.summaryInvoices = data.data || []
					this.summaryTruncated = data.total > this.summaryLimit
					this.calculateSummaryTotals()
					this.summaryLoading = false
				})
				.catch(error => {
					this.summaryLoading = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'Error loading recurring summary'),
						timer: 6000,
					})
				})
		},

		calculateSummaryTotals() {
			const invoices = this.summaryInvoices || []
			const totals = {
				current: 0,
				all: 0,
				rangeUnder: 0,
				rangeOver: 0,
			}

			if (invoices.length === 0) {
				this.summaryTotals = totals
				return
			}

			const today = new Date()
			const rangeStartDateStr = this.customRangeStart || this.todayDate
			const rangeStartYear = this.getYearFromDate(rangeStartDateStr) || today.getFullYear()
			let rangeEndDateStr = this.customRangeEnd || this.maxContractEndDate
			let rangeEndYear = rangeEndDateStr ? this.getYearFromDate(rangeEndDateStr) : null

			if (!rangeEndYear) {
				rangeEndYear = rangeStartYear
			}

			const rangeYears = Math.max(0, rangeEndYear - rangeStartYear + 1)

			let maxContractEnd = this.maxContractEndDate

			invoices.forEach(inv => {
				if (inv.frequency !== 'y' || inv.disable) return
				const annualAmount = this.getAnnualAmount(inv)
				if (annualAmount <= 0) return

				totals.current += annualAmount

				const client = inv.client || {}
				let contractStart = this.getYearFromDate(client.contract_start_at)
				let contractEnd = this.getYearFromDate(client.contract_ends_at)

				if (client.contract_ends_at) {
					const endStr = this.getLocalDateString(new Date(client.contract_ends_at))
					if (!maxContractEnd || endStr > maxContractEnd) {
						maxContractEnd = endStr
					}
				}

				if (!contractStart && !contractEnd) {
					contractStart = rangeStartYear
					contractEnd = rangeStartYear
				} else if (contractStart && !contractEnd) {
					contractEnd = contractStart
				} else if (!contractStart && contractEnd) {
					contractStart = contractEnd
				}

				let contractYears = 1
				if (contractStart && contractEnd && contractEnd >= contractStart) {
					contractYears = contractEnd - contractStart + 1
				}
				totals.all += annualAmount * contractYears

				if (rangeYears > 0 && contractStart && contractEnd) {
					const overlapStart = Math.max(rangeStartYear, contractStart)
					const overlapEnd = Math.min(rangeEndYear, contractEnd)
					const underYears = overlapStart <= overlapEnd ? overlapEnd - overlapStart + 1 : 0
					const overYears = Math.max(0, rangeYears - underYears)
					totals.rangeUnder += annualAmount * underYears
					totals.rangeOver += annualAmount * overYears
				}
			})

			if (!this.customRangeEnd && maxContractEnd) {
				this.maxContractEndDate = maxContractEnd
				this.customRangeEnd = maxContractEnd
			} else if (maxContractEnd) {
				this.maxContractEndDate = maxContractEnd
			}

			this.summaryTotals = totals
		},

		getAnnualAmount(inv) {
			const amount = parseFloat(inv.amount) || 0
			const frequency = inv.frequency || 'y'
			const factor = {
				y: 1,
				m: 12,
				q: 4,
				w: 52,
				'2w': 26,
			}[frequency] || 1
			return amount * factor
		},

		getLocalDateString(date) {
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			return `${year}-${month}-${day}`
		},

		getYearFromDate(dateValue) {
			if (!dateValue) return null
			const date = new Date(dateValue)
			if (Number.isNaN(date.getTime())) return null
			return date.getFullYear()
		},

		// Get items (called from pagination component)
		getItems(page) {
			this.updateUrlParams({ page })
		},

		// Get filters from LPDataTableFilters component
		getFilters(filters) {
			this.params = {}
			filters.forEach(filter => {
				if (filter.value && filter.value !== '' && filter.value.length > 0) {
					this.params[filter.name] = filter.value
				} else {
					delete this.params[filter.name]
				}
			})
			this.$router.push({ query: { ...this.params } })
		},

		// Clear all filters
		clearFilters() {
			this.params = {}
			this.table.filters.forEach(filter => {
				filter.value = ''
			})
			this.updateUrlParams({})
		},

		// Check if any filters are active
		hasActiveFilters() {
			return Object.keys(this.params).length > 0
		},

		// Update URL parameters
		updateUrlParams(params) {
			const query = { ...this.$route.query }

			// Remove all existing filter params if clearing
			if (Object.keys(params).length === 0) {
				this.$router.push({ query: {} })
				return
			}

			// Handle page parameter
			if (params.page !== undefined) {
				if (params.page === 1) {
					delete query.page
				} else {
					query.page = params.page
				}
			}

			// Handle other parameters
			Object.keys(params).forEach(key => {
				if (key !== 'page') {
					if (params[key] === '' || params[key] === null || params[key] === undefined) {
						delete query[key]
					} else {
						query[key] = params[key]
					}
				}
			})

			this.$router.push({ query })
		},

		// Toggle user column
		toggleUserColumn(column) {
			if (this.userColumns.includes(column)) {
				this.userColumns = this.userColumns.filter(c => c !== column)
			} else {
				this.userColumns.push(column)
			}
			this.updateUserColumns()
		},

		// Update user columns
		updateUserColumns() {
			localStorage.setItem('recurring-invoices-columns', JSON.stringify(this.userColumns))
		},

		// Set user per page
		setUserPerPage() {
			localStorage.setItem('recurring-invoices-per-page', this.userPerPage)
			this.getRecurringInvoices()
		},

		// Format currency
		formatCurrency(amount, currencyCode = 'USD') {
			if (!amount) return 'N/A'
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: currencyCode,
			}).format(amount)
		},

		// Format date
		formatDate(date) {
			if (!date) return 'N/A'
			return new Date(date).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			})
		},

		formatDateOrTilde(date) {
			if (!date) return '~'
			const parsed = new Date(date)
			if (Number.isNaN(parsed.getTime())) return '~'
			return parsed.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			})
		},

		formatRemaining(value) {
			return value || '~'
		},

		// Get frequency label
		getFrequencyLabel(frequency) {
			const labels = {
				y: 'Yearly',
				m: 'Monthly',
				'2w': 'Bi-Weekly',
				w: 'Weekly',
				q: 'Quarterly',
			}
			return labels[frequency] || frequency
		},

		// Truncate text
		truncateText(text, length) {
			if (!text) return 'N/A'
			if (text.length <= length) return text
			return text.substring(0, length) + '...'
		},
	},
}
</script>

<style scoped>
.bg-secondary-lighter {
	background-color: #f8f9fa;
	opacity: 0.8;
}

.summary-card {
	border: 1px solid #e9ecef;
	border-radius: 8px;
	padding: 12px;
	background: #ffffff;
}

.summary-label {
	font-size: 12px;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #6c757d;
}

.summary-value {
	font-size: 20px;
	font-weight: 700;
	color: #212529;
	margin-top: 4px;
}

.summary-subtext {
	font-size: 12px;
	color: #6c757d;
	margin-top: 4px;
}

.row-missing-client {
	background-color: #f8d7da;
}

.detail-label {
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #6c757d;
}

.detail-value {
	font-size: 14px;
	font-weight: 600;
	color: #212529;
}

.response-box {
	background: #f8f9fa;
	border: 1px solid #e9ecef;
	border-radius: 6px;
	padding: 10px;
	font-size: 12px;
	max-height: 260px;
	overflow: auto;
	white-space: pre-wrap;
}
</style>
